import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination, TextField, InputAdornment,
  Select, MenuItem, IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Box, Chip, Alert,
} from '@mui/material';
import { Search, DeleteOutline, ShareOutlined, History as HistoryIcon, AddCircleOutline, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAnalysis } from '../AnalysisContext';
import { PageHeader, EmptyState, NameAvatar, formatDate, formatDateTime } from '../components/ui';

export default function History() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uploads, loadingLists, loadUpload, deleteUpload, shareAnalysis } = useAnalysis();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [toDelete, setToDelete] = useState(null);
  const [toShare, setToShare] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMsg, setShareMsg] = useState(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = uploads.filter(u => !q || (u.customerName || '').toLowerCase().includes(q));
    return filtered.sort((a, b) => (sort === 'newest' ? 1 : -1) * (new Date(b.uploadDate || 0) - new Date(a.uploadDate || 0)));
  }, [uploads, query, sort]);
  const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const open = async (u) => { if (await loadUpload(u.uploadId, u.customerName || 'Saved analysis')) navigate('/'); };
  const doShare = async () => {
    const r = await shareAnalysis(toShare.uploadId, shareEmail);
    setShareMsg(r);
    if (r.ok) setTimeout(() => { setToShare(null); setShareEmail(''); setShareMsg(null); }, 900);
  };

  return (
    <>
      <PageHeader
        title={t('nav.history', 'History')}
        subtitle={t('history.subtitle', 'Your saved analyses. Analyses are kept for 180 days.')}
        actions={<Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => navigate('/')}>{t('nav.newAnalysis', 'New Analysis')}</Button>}
      />

      {!loadingLists && uploads.length === 0 ? (
        <EmptyState
          icon={<HistoryIcon />}
          title={t('history.emptyTitle', 'No saved analyses yet')}
          text={t('history.emptyText', 'Analyses you run are saved here automatically so you can open, share or compare them later.')}
          action={<Button variant="contained" onClick={() => navigate('/')}>{t('history.start', 'Run your first analysis')}</Button>}
        />
      ) : (
        <Paper variant="outlined">
          <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              placeholder={t('history.search', 'Search customer or workload…')}
              value={query}
              onChange={e => { setQuery(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 280, flex: { xs: 1, sm: 'none' } }}
            />
            <Select size="small" value={sort} onChange={e => setSort(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="newest">{t('history.newest', 'Newest first')}</MenuItem>
              <MenuItem value="oldest">{t('history.oldest', 'Oldest first')}</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{rows.length} {t('history.analyses', 'analyses')}</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('history.customer', 'Customer / workload')}</TableCell>
                  <TableCell align="center">{t('history.servers', 'Servers')}</TableCell>
                  <TableCell>{t('history.uploaded', 'Uploaded')}</TableCell>
                  <TableCell align="right">{t('history.actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((u, i) => (
                  <TableRow key={u.uploadId} hover sx={{ cursor: 'pointer' }} onClick={() => open(u)} style={{ animation: 'fadeIn .5s ease both', animationDelay: `${Math.min(i, 12) * 45}ms` }}>
                    <TableCell sx={{ fontWeight: 600 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><NameAvatar name={u.customerName} />{u.customerName || 'Analysis'}</Box></TableCell>
                    <TableCell align="center"><Chip size="small" label={u.serverCount || 1} color={(u.serverCount || 1) > 1 ? 'secondary' : 'default'} variant={(u.serverCount || 1) > 1 ? 'filled' : 'outlined'} /></TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(u.uploadDate)}><span>{formatDate(u.uploadDate)}</span></Tooltip>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Tooltip title={t('history.open', 'Open')}><IconButton size="small" onClick={() => open(u)}><OpenInNew fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={t('history.share', 'Share')}><IconButton size="small" onClick={() => { setToShare(u); setShareMsg(null); }}><ShareOutlined fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={t('history.delete', 'Delete')}><IconButton size="small" color="error" onClick={() => setToDelete(u)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {pageRows.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('history.noMatch', 'No analyses match your search.')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}

      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogTitle>{t('history.deleteTitle', 'Delete this analysis?')}</DialogTitle>
        <DialogContent><DialogContentText>{toDelete?.customerName || 'Analysis'} ({formatDate(toDelete?.uploadDate)}) {t('history.deleteText', 'will be removed from your history. This cannot be undone.')}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={async () => { await deleteUpload(toDelete.uploadId); setToDelete(null); }}>{t('history.delete', 'Delete')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toShare} onClose={() => setToShare(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('history.shareTitle', 'Share analysis')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{toShare?.customerName || 'Analysis'} — {t('history.shareText', 'the person you enter will see it under "Shared with me".')}</DialogContentText>
          <TextField autoFocus fullWidth type="email" label={t('history.email', 'Email address')} value={shareEmail} onChange={e => setShareEmail(e.target.value)} />
          {shareMsg && <Alert severity={shareMsg.ok ? 'success' : 'error'} sx={{ mt: 2 }}>{shareMsg.message}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToShare(null)}>{t('common.close', 'Close')}</Button>
          <Button variant="contained" disabled={!shareEmail.trim()} onClick={doShare}>{t('history.share', 'Share')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
